import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiEye } from 'react-icons/fi';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import TaskModal from '../../components/TaskModal';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../services/api';
import '../../styles/trello-board.css';

const ManagerTasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState({
    todo: {
      id: 'todo',
      title: 'To Do',
      taskIds: []
    },
    in_progress: {
      id: 'in_progress',
      title: 'In Progress',
      taskIds: []
    },
    testing: {
      id: 'testing',
      title: 'Testing',
      taskIds: []
    },
    client_review: {
      id: 'client_review',
      title: 'Client Review',
      taskIds: []
    },
    completed: {
      id: 'completed',
      title: 'Completed',
      taskIds: []
    }
  });
  const [columnOrder, setColumnOrder] = useState(['todo', 'in_progress', 'testing', 'client_review', 'completed']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');

  useEffect(() => {
    fetchTasks();
    fetchUsers();
    fetchClients();
    fetchProjects();
  }, []);

  const fetchTasks = async (projectId = '') => {
    try {
      setLoading(true);
      const url = projectId ? `/manager/tasks?project_id=${projectId}` : '/manager/tasks';
      console.log('Fetching tasks from URL:', url);
      const response = await api.get(url);
      const fetchedTasks = response.data;
      console.log('Fetched tasks:', fetchedTasks.length, 'tasks');
      
      // Organize tasks into columns
      const todoTasks = [];
      const inProgressTasks = [];
      const testingTasks = [];
      const clientReviewTasks = [];
      const completedTasks = [];
      
      fetchedTasks.forEach(task => {
        switch(task.status) {
          case 'todo':
            todoTasks.push(task.id);
            break;
          case 'in_progress':
            inProgressTasks.push(task.id);
            break;
          case 'testing':
            testingTasks.push(task.id);
            break;
          case 'client_review':
            clientReviewTasks.push(task.id);
            break;
          case 'completed':
            completedTasks.push(task.id);
            break;
          default:
            todoTasks.push(task.id);
        }
      });
      
      setTasks(fetchedTasks.reduce((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}));
      
      setColumns({
        todo: {
          id: 'todo',
          title: 'To Do',
          taskIds: todoTasks
        },
        in_progress: {
          id: 'in_progress',
          title: 'In Progress',
          taskIds: inProgressTasks
        },
        testing: {
          id: 'testing',
          title: 'Testing',
          taskIds: testingTasks
        },
        client_review: {
          id: 'client_review',
          title: 'Client Review',
          taskIds: clientReviewTasks
        },
        completed: {
          id: 'completed',
          title: 'Completed',
          taskIds: completedTasks
        }
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again.');
      setLoading(false);
    }
  };
  
  const fetchProjects = async () => {
    try {
      const response = await api.get('/manager/projects');
      setProjects(response.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users', {
        params: { limit: 1000 }
      });
      setUsers(response.data.users || response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const handleCreateTask = () => {
    if (selectedProject) {
      const selectedProjectData = projects.find(p => p.id === parseInt(selectedProject));
      const prefilledTask = {
        project_id: selectedProjectData?.id,
        client_id: selectedProjectData?.client_id || null
      };
      setCurrentTask(prefilledTask);
    } else {
      setCurrentTask(null);
    }
    setShowTaskModal(true);
  };

  const handleEditTask = (task) => {
    setCurrentTask(task);
    setShowTaskModal(true);
  };
  
  const handleDeleteTask = (task) => {
    setCurrentTask(task);
    setShowDeleteModal(true);
  };

  const handleSaveTask = async (taskData) => {
    try {
      if (currentTask && currentTask.id) {
        await api.put(`/manager/tasks/${currentTask.id}`, taskData);
      } else {
        await api.post('/manager/tasks', taskData);
      }
      setShowTaskModal(false);
      await fetchTasks(selectedProject);
    } catch (err) {
      console.error('Error saving task:', err);
      alert('Failed to save task: ' + (err.response?.data?.message || err.message));
    }
  };
  
  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    
    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      return;
    }
    
    const taskId = draggableId;
    const task = tasks[taskId];
    
    if (!task) {
      console.error('Task not found in local state:', taskId);
      await fetchTasks(selectedProject);
      return;
    }
    
    if (destination.droppableId !== source.droppableId) {
      try {
        await api.put(`/manager/tasks/${taskId}`, {
          status: destination.droppableId
        });
        
        const newTasks = {
          ...tasks,
          [taskId]: {
            ...task,
            status: destination.droppableId
          }
        };
        setTasks(newTasks);
      } catch (err) {
        console.error('Error updating task status:', err);
        alert('Failed to update task status: ' + (err.response?.data?.message || err.message));
        fetchTasks(selectedProject);
        return;
      }
    }
    
    const startColumn = columns[source.droppableId];
    const endColumn = columns[destination.droppableId];
    
    if (startColumn === endColumn) {
      const newTaskIds = Array.from(startColumn.taskIds);
      newTaskIds.splice(source.index, 1);
      newTaskIds.splice(destination.index, 0, taskId);
      
      const newColumn = {
        ...startColumn,
        taskIds: newTaskIds
      };
      
      setColumns({
        ...columns,
        [newColumn.id]: newColumn
      });
    } else {
      const startTaskIds = Array.from(startColumn.taskIds);
      startTaskIds.splice(source.index, 1);
      
      const newStartColumn = {
        ...startColumn,
        taskIds: startTaskIds
      };
      
      const endTaskIds = Array.from(endColumn.taskIds);
      endTaskIds.splice(destination.index, 0, taskId);
      
      const newEndColumn = {
        ...endColumn,
        taskIds: endTaskIds
      };
      
      setColumns({
        ...columns,
        [newStartColumn.id]: newStartColumn,
        [newEndColumn.id]: newEndColumn
      });
    }
  };
  
  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/tasks/${currentTask.id}`);
      setShowDeleteModal(false);
      fetchTasks(selectedProject);
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task: ' + (err.response?.data?.message || err.message));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Tasks</h1>
        <div className="text-center py-8">Loading tasks...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Tasks</h1>
        <div className="text-center py-8 text-red-500">{error}</div>
        <button 
          onClick={fetchTasks} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Tasks</h1>
        <div className="flex items-center space-x-4">
          <select
            value={selectedProject}
            onChange={(e) => {
              const projectId = e.target.value;
              console.log('Project selected:', projectId);
              setSelectedProject(projectId);
              fetchTasks(projectId);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Projects</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <button
            onClick={handleCreateTask}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
          >
            <FiPlus className="mr-2" /> Create Task
          </button>
        </div>
      </div>

      {Object.keys(tasks).length === 0 ? (
        <div className="text-center py-8 bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 mb-4">No tasks found</p>
          <button
            onClick={handleCreateTask}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Create Your First Task
          </button>
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="board-container">
            {columnOrder.map(columnId => {
              const column = columns[columnId];
              const columnTasks = column.taskIds.map(taskId => tasks[taskId]).filter(Boolean);
              
              return (
                <div key={column.id} className="board-column">
                  <h2 className="board-column-title">{column.title}</h2>
                  
                  <Droppable droppableId={column.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`task-list ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                      >
                        {columnTasks.map((task, index) => (
                          <Draggable key={task.id} draggableId={task.id.toString()} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`task-card ${snapshot.isDragging ? 'dragging' : ''}`}
                              >
                                <div className="task-card-header">
                                  <h3 className="task-card-title">{task.title}</h3>
                                  <div className="task-card-actions">
                                    <Link
                                      to={`/manager/tasks/${task.id}`}
                                      className="text-blue-600 hover:text-blue-900 p-1"
                                    >
                                      <FiEye size={14} />
                                    </Link>
                                    <button
                                      onClick={() => handleEditTask(task)}
                                      className="text-indigo-600 hover:text-indigo-900 p-1"
                                    >
                                      <FiEdit2 size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTask(task)}
                                      className="text-red-600 hover:text-red-900 p-1"
                                    >
                                      <FiTrash2 size={14} />
                                    </button>
                                  </div>
                                </div>
                                
                                {task.description && (
                                  <div className="text-xs text-gray-600 mb-2 line-clamp-2">
                                    {task.description.replace(/<[^>]*>/g, ' ').trim()}
                                  </div>
                                )}
                                
                                <div className="task-card-footer">
                                  <span className={`task-priority-badge priority-${task.priority}`}>
                                    {task.priority}
                                  </span>
                                  <span className="task-due-date">
                                    {formatDate(task.due_date)}
                                  </span>
                                </div>
                                
                                {task.Client && (
                                  <div className="task-meta">
                                    Client: {task.Client.name}
                                  </div>
                                )}
                                
                                {task.Project && (
                                  <div className="task-meta">
                                    Project: {task.Project.name}
                                  </div>
                                )}
                                
                                {task.Users && task.Users.length > 0 && (
                                  <div className="task-meta">
                                    Assigned: {task.Users.map(user => user.name).join(', ')}
                                  </div>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}

      {showTaskModal && (
        <TaskModal
          task={currentTask}
          onClose={() => setShowTaskModal(false)}
          onSave={handleSaveTask}
          users={users}
          clients={clients}
          projects={projects}
        />
      )}

      {showDeleteModal && (
        <ConfirmModal
          title="Delete Task"
          message={`Are you sure you want to delete "${currentTask.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default ManagerTasksPage;